// src/pages/LoginPage.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import Seo from './Seo';
import './AuthForm.css';
import axios from 'axios';
import API_BASE_URL from '../config';
import { initializeGoogleSignIn, renderGoogleButton } from '../services/googleAuthService';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = API_BASE_URL;

// Pick the right landing page for a freshly-logged-in user.
function landingFor(user) {
  if (!user) return '/dashboard';
  if (user.is_staff || user.is_superuser || user.role === 'admin') return '/admin';
  if (user.role === 'merchant') return '/merchant';
  if (user.role === 'pending_merchant') return '/business/pending';
  return '/dashboard';
}

function LoginPage({ setIsAuthenticated }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { refresh } = useAuth();

  // Initialize Google Sign-In
  useEffect(() => {
    const handleGoogleSuccess = async (data) => {
      setIsAuthenticated(true);
      // Pull fresh role + owned_stores into AuthContext, then route by role.
      await refresh();
      // The data payload from Google login may already include user; fall back
      // to /dashboard if we couldn't determine role.
      navigate(landingFor(data?.user || data));
    };

    const handleGoogleError = (error) => {
      alert(error || 'Google sign-in failed');
    };

    // Wait for Google SDK to load
    const initGoogle = () => {
      if (window.google) {
        initializeGoogleSignIn(handleGoogleSuccess, handleGoogleError);
        renderGoogleButton('googleSignInButton', {
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
        });
      } else {
        // Retry after a short delay
        setTimeout(initGoogle, 100);
      }
    };

    initGoogle();
    // refresh is a stable useCallback from AuthContext; depending on it would
    // create a re-init loop without changing behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, setIsAuthenticated]);

  const handleLogin = async () => {
    if (!username || !password) {
      alert('Please enter both username and password');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/login/`, {
        email: username,
        password
      });

      const { access_token, refresh_token, user } = response.data;

      localStorage.setItem('accessToken', access_token);
      localStorage.setItem('refreshToken', refresh_token);
      localStorage.setItem('userEmail', username);

      setIsAuthenticated(true);
      // Refresh AuthContext so RequireRole & RoleHomeRedirect see the new
      // role before we navigate. Then route to the role-appropriate home.
      await refresh();
      navigate(landingFor(user));
    } catch (err) {
      console.error('Login failed:', err.response?.data || err.message);
      alert(err.response?.data?.message || 'Invalid credentials or server error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-wrapper">
      <Seo path="/login" title="Log in — Tudu" description="Log in to your Tudu account to manage tasks, routines, deals, and more." noindex />
      <div className="auth-container">
        <h2>Login</h2>
        
        {/* Google Sign-In Button */}
        <div id="googleSignInButton" style={{ 
          marginBottom: '20px', 
          display: 'flex', 
          justifyContent: 'center' 
        }}></div>
        
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          margin: '20px 0',
          color: '#666'
        }}>
          <div style={{ flex: 1, height: '1px', background: '#ddd' }}></div>
          <span style={{ padding: '0 15px', fontSize: '14px' }}>or</span>
          <div style={{ flex: 1, height: '1px', background: '#ddd' }}></div>
        </div>

        <input
          className="auth-input"
          placeholder="Username or Email"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        <div style={{ position: 'relative', marginBottom: '1.2rem' }}>
          <input
            className="auth-input"
            type={showPassword ? 'text' : 'password'}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(); }}
            style={{ marginBottom: 0, paddingRight: '48px' }}
          />
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            style={{
              position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)',
              background: 'transparent', border: 'none', cursor: 'pointer', color: '#888',
              display: 'inline-flex', alignItems: 'center', padding: '4px',
            }}
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
        <button className="auth-btn" onClick={handleLogin} disabled={loading}>
          {loading ? 'Logging in...' : 'Login'}
        </button>
        <div className="auth-links">
          <Link to="/signup">Don't have an account? Sign up</Link>
          <Link to="#">Forgot Password?</Link>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
